import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {BaseProtocolFormComponent} from "../base-protocol-form/base-protocol-form.component";
import {CustomItemCreatingEvent} from "devextreme/ui/select_box";
import * as uuid from "uuid";
import {DxDataGridComponent} from "devextreme-angular";
import {Metric, MetricGroup} from "../../../../../models/device-type/DeviceType";
import {DataType} from "../../../../../models/metric/DataType";

@Component({
  selector: 'app-s7-metric-table',
  templateUrl: './s7-metric-table.component.html',
  styleUrls: ['./s7-metric-table.component.css']
})
export class S7MetricTableComponent extends BaseProtocolFormComponent {

  @ViewChild('editMetricDataGrid', {static: false}) dataGrid?: DxDataGridComponent;

  protected selectedMetricRow: any[] = [];

  private _metricIsBeingEdited!: boolean;
  private _isEditMode!: boolean;
  private _customDataUnitId: number = -1;

  protected currentRowData: any;

  protected PROTOCOL: string = 'S7';

  @Output() customDataUnitChanged: EventEmitter<any> = new EventEmitter();

  get customDataUnitId(): number {
    return this._customDataUnitId;
  }

  @Input() set customDataUnitId(value: number) {
    this._customDataUnitId = value;
    this.customDataUnitChanged.emit(this.customDataUnitId);
  }

  @Output() isEditModeChange: EventEmitter<any> = new EventEmitter();

  get isEditMode(): boolean {
    return this._isEditMode;
  }

  @Input() set isEditMode(value: boolean) {
    this._isEditMode = value;
    this.isEditModeChange.emit(this.isEditMode);
  }

  @Output() metricIsBeingEditedChange: EventEmitter<any> = new EventEmitter();

  get metricIsBeingEdited(): boolean {
    return this._metricIsBeingEdited;
  }

  @Input() set metricIsBeingEdited(value: boolean) {
    this._metricIsBeingEdited = value;
    this.metricIsBeingEditedChange.emit(this.metricIsBeingEdited);
  }

  protected fields: string[] = [
    'name',
    'transportConfiguration.address',
    'transportConfiguration.dataType',
    'transportConfiguration.bitPosition',
    'transportConfiguration.byteOrder',
    'divider',
    'dataUnit.name',
    'decimalDigits'
  ];
  protected captions: string[] = [
    this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.NAME'),
    this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.ADDRESS'),
    this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.DATA_TYPE'),
    this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.BIT_POSITION'),
    this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.BYTE_ORDER'),
    this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.DIVIDER'),
    this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.DATA_UNIT'),
    this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.DECIMAL_DIGITS')
  ];
  protected fieldTypes = ['string', 'float', 'string', 'float', 'string', 'float', 'dataUnit', 'float'];


  constructor() {
    super();
    this.metricIsBeingEdited = false;
    this.isEditMode = false;
  }

  async metricEditStart($event: any) {
    if (!this.metricIsBeingEdited) {
      this.metricIsBeingEdited = true;
      $event.component.clearSelection();

    } else {
      $event.cancel = true;
      const dialogResult = await this.showDiscardEditsDialogWithPromise();
      if (dialogResult) {
        this.metricIsBeingEdited = false;

        $event.component.editRow($event.component.getRowIndexByKey($event.data.id));

      }
    }
  }

  async metricEditEnd($event: any) {
    this.metricIsBeingEdited = false;
    this.dataGrid?.instance.columnOption("dataUnit.id", "lookup", {
      dataSource: this.dataUnitValues,
      displayExpr: "name",
      valueExpr: "id"
    });
  }

  editMetricAddRowButton($event: any) {
    const toolbarItems = $event.toolbarOptions.items;
    toolbarItems.forEach((item: any) => {
      if (item.name === 'addRowButton') {
        item.options = {
          icon: 'add',
          onClick: () => this.customMetricAddRow($event)
        };
      }
    });
  }

  setCellValue(newData: any, value: any) {
    let column = (<any>this);
    column.defaultSetCellValue(newData, value);
  }

  onEditorPreparingMetric(e: any) {
    if (e.parentType === 'dataRow') {

      if (e.row?.data?.transportConfiguration?.dataType) {
        const fullDataType = this.getFullDataType(e.row.data.transportConfiguration.dataType);

        if (e.dataField === 'transportConfiguration.bitPosition') {
          const disabled = !fullDataType?.supportsBitPosition;
          e.editorOptions.disabled = disabled;
          e.editorOptions.placeholder = disabled ? this.translate.instant("COMMON.NOT_AVAILABLE") : '';
          if (disabled && e.value !== null) {
            e.setValue(null); // Resetta il valore di bitPosition
            e.component.refresh();
          }
        }

        if (e.dataField === 'dataUnit.id') {
          const disabled = !fullDataType?.supportsUnit;
          e.editorOptions.disabled = disabled;
          e.editorOptions.placeholder = disabled ? this.translate.instant("COMMON.NOT_AVAILABLE") : '';
          if (disabled && e.value !== null) {
            e.setValue(null);
            e.component.refresh();
          }
        }

        if (e.dataField === 'decimalDigits') {
          const disabled = !fullDataType?.supportsDecimals;
          e.editorOptions.disabled = disabled;
          e.editorOptions.placeholder = disabled ? this.translate.instant("COMMON.NOT_AVAILABLE") : '';
          if (disabled && e.value !== null) {
            e.setValue(null);
            e.component.refresh();
          }
        }

        if (e.dataField === 'divider') {
          const disabled = !fullDataType?.supportsDivisor;
          e.editorOptions.disabled = disabled;
          e.editorOptions.placeholder = disabled ? this.translate.instant("COMMON.NOT_AVAILABLE") : '';
          if (disabled && e.value !== null) {
            e.setValue(null);
            e.component.refresh();
          }
        }
      }

      if (e.dataField === 'dataUnit.id') {
        this.currentRowData = e.row.data;
      }
    }
  }

  addNewDataUnit = (onCustomItemCreatingEvent: CustomItemCreatingEvent) => {

    if (!onCustomItemCreatingEvent.text) {
      return;
    }

    const component = onCustomItemCreatingEvent.component;
    const dataSource = component.getDataSource();
    const dataStore = dataSource.store();

    // Cerca se l'item esiste già nel data store
    dataStore.load().then((items: any) => {
      let existingItem = items.find((item: any) => item.name === onCustomItemCreatingEvent.text);

      if (existingItem) {
        // Se l'item esiste già, assegnalo all'evento
        onCustomItemCreatingEvent.customItem = existingItem;
      } else {
        // Se l'item non esiste, creane uno nuovo
        let newItem = {
          id: this.customDataUnitId--,
          name: onCustomItemCreatingEvent.text
        };

        dataStore.insert(newItem).then(
          (insertedItem: any) => {
            onCustomItemCreatingEvent.customItem = insertedItem;
            this.dataUnitValuesChange.emit(this.dataUnitValues);
          }
        );
      }

      // Ricarica manualmente la colonna di lookup (il data grid non lo fa automaticamente)

    });
  };

  calculateMetricValue($event: any) {
    return $event.name;
  }

  dividerRange = (validationRequest: any) => {
    return validationRequest.value != 0;

  };

  validateDivider = (validationRequest: any) => {
    if (validationRequest?.data?.transportConfiguration?.dataType &&
      this.getFullDataType(validationRequest.data.transportConfiguration.dataType)?.supportsDivisor) {
      return validationRequest.value !== null && validationRequest.value !== undefined
    }
    return true
  };

  validateBit = (validationRequest: any) => {
    if (
      validationRequest?.data?.transportConfiguration?.dataType &&
      this.getFullDataType(validationRequest.data.transportConfiguration.dataType)?.supportsBitPosition
    ) {
      if (validationRequest.value === null || validationRequest.value === undefined) {
        return false;
      }
      const max = this.getMaxBit(validationRequest.data);
      return validationRequest.value >= 0 && validationRequest.value <= max;
    }
    return true;
  };

  getMaxBit(row: any): number {
    return row?.transportConfiguration?.dataType === 'BIT32' ? 31 : 15;
  }

  duplicateMetric(metric: Metric) {
    const metrics = this.groupMetric!.metrics!;
    const index = metrics.indexOf(metric);
    const clone = this.cloneMetric(metric, metrics.map((m: Metric) => m.name));
    metrics.splice(index + 1, 0, clone);
    this.selectedMetricRow = [(clone as any).id];
    this.dataGrid?.instance.refresh();
    this.checkEditDeviceTypeValidation.emit();
  }

  onDuplicateMetricClick = (e: any) => {
    e.event?.stopPropagation();
    this.duplicateMetric(e.row.data);
  };

  customMetricAddRow($event: any) {
    if (!this.metricIsBeingEdited) {
      $event.component.addRow().done(() => {
        this.metricIsBeingEdited = true;
        $event.component.clearSelection();
      });
    } else {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          $event.component.addRow().done(() => {
            this.metricIsBeingEdited = true;
            $event.component.clearSelection();
          });
        }
      });
    }
  }

  /**
   * Safely gets the value of a nested property from an object using dot notation.
   */
  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc && acc[key], obj);
  }

  /**
   * Converts a list of objects to a CSV string using ";" as the separator.
   * @returns CSV string with headers on the first row.
   * @param data
   * @param filename
   */
  objectsToCSV(data: any[], filename: string = 'data.csv') {

    if (!this.fields.length) {
      console.warn('No data or fields provided for CSV download.');
      return;
    }

    const csvRows: string[] = [];

    // Header row
    csvRows.push(this.captions.join(';'));

    // Data rows
    for (const row of data) {
      const csvRow = this.fields.map(field => {
        const value = this.getNestedValue(row, field);
        const escaped = String(value ?? '').replace(/"/g, '""'); // Escape double quotes
        return `${escaped}`;
      });
      csvRows.push(csvRow.join(';'));
    }

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  triggerFileSelection(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any): void {
    const file = event.srcElement.files[0]; // Assuming single file selection
    if (file) {
      try {
        this.parseCSV(file);
      } catch (e: any) {
        this.toastService.openErrorToastMessage(e);
        this.logger.error(e);
      }
    }
    event.target.value = '';
  }

  private parseCSV(file: File): void {
    const reader = new FileReader();
    const data: any[] = [];

    reader.onload = (e: any) => {
      const csvData = e.target.result;
      if (!csvData) {
        this.toastService.openErrorToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.FILE_EMPTY'));
        return;
      }
      const csvRows: any[] = csvData.split('\n').slice(1); // Skip header row

      for (const row of csvRows) {
        if (!row.trim()) continue; // Skip empty lines
        const cells = row.split(';');
        let current: any = {};

        if (cells.length !== this.fields.length) {
          this.toastService.openErrorToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.INVALID_CSV_LENGTH', [csvRows.indexOf(row)]));
          return
        }

        for (let index = 0; index < cells.length; index++) {
          const cell = cells[index];
          const fieldPath = this.fields[index];
          if (!fieldPath) continue;

          const keys = fieldPath.split('.');
          const trimmed = cell.trim();
          const fieldType = this.fieldTypes[index];

          if (fieldType === 'string') {
            current = this.extractValue(keys, current, trimmed);
          } else if (fieldType === 'float') {
            let num: any = parseFloat(trimmed);
            if (trimmed === "") {
              num = null
            }
            if (isNaN(num)) {
              this.toastService.openErrorToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.INVALID_CSV', [csvRows.indexOf(row)]));
              return; // <-- now it will actually exit
            }
            current = this.extractValue(keys, current, num);
          } else if (fieldType === 'dataUnit') {
            let selectedDataUnit = this.dataUnitValues.find((item: any) => item.name === trimmed);
            if (selectedDataUnit) {
              current["dataUnit"] = selectedDataUnit;
            } else if (!!trimmed) {
              this.toastService.openInfoToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.UNIT_DISCARDED', [csvRows.indexOf(row) + 2]));
            }
          }
        }
        current["id"] = "a" + uuid.v4();
        data.push(current);
      }
      this.updateAndValidateGrid(data);
      this.cdr.detectChanges()
    };
    reader.readAsText(file);
  }

  validateCsv(newMetrics: Metric[]): [number, string] {
    for (let index = 0; index < newMetrics.length; index++) {
      const metric = newMetrics[index];
      const transportConfig = metric.transportConfiguration;
      if (!metric.name) return [index, 'NAME'];
      if (transportConfig.address === undefined || transportConfig.address === null) return [index, 'ADDRESS'];
      if (transportConfig.address < 0 || transportConfig.address > 65535) return [index, 'ADDRESS'];
      if (!this.gmService.s7DataTypeValues.find((x: any) => x.value === transportConfig.dataType)) return [index, 'DATA_TYPE'];
      const fullDataType = this.getFullDataType(transportConfig.dataType);
      if (fullDataType?.supportsBitPosition) {
        if (transportConfig.bitPosition === undefined || transportConfig.bitPosition === null) return [index, 'BIT_POSITION'];
        const maxBitPosition = transportConfig.dataType === 'BIT32' ? 31 : 15;
        if (transportConfig.bitPosition < 0 || transportConfig.bitPosition > maxBitPosition) return [index, 'BIT_POSITION'];
      }
      if (fullDataType?.supportsDivisor) {
        if (metric.divider === undefined || metric.divider === null) return [index, 'DIVIDER'];
        if (metric.divider < 0.000001) return [index, 'DIVIDER'];
      }
      if (!this.gmService.byteOrderValues.find((x: any) => x.value === transportConfig.byteOrder)) return [index, 'BYTE_ORDER'];
      if (metric.decimalDigits !== undefined && (metric.decimalDigits < this.gmService.DECIMAL_DIGITS_MIN || metric.decimalDigits > this.gmService.DECIMAL_DIGITS_MAX)) {
        return [index, 'DECIMAL_DIGITS'];
      }
    }
    return [-1, ""];
  }


  // Your method to update and validate the grid
  updateAndValidateGrid(newData: Metric[]): void {
    const [lineWithError, columnWithError] = this.validateCsv(newData);
    if (lineWithError !== -1) {
      this.toastService.openErrorToastMessage(this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.INVALID_CSV_COL', [lineWithError + 2, this.translate.instant('REGISTRY.DEVICE_TYPE.METRIC.' + columnWithError.toUpperCase())]));
      return;
    }
    if (this.dataGrid?.instance) {
      // Clean up unwanted fields
      this.cleanUpCsvData(newData, this.gmService.s7DataTypeValues);
      this.markCsvImportedRows(this.groupMetric?.metrics, newData);

      // Update the grid with new data
      this.dataGrid.instance.option('dataSource', newData);
      this.groupMetric!.metrics = newData;

      // This is how you actually validate all rows
      this.checkEditDeviceTypeValidation.emit();

      // Need to refresh before validating
      this.dataGrid.instance.refresh();
    }
  }

  private extractValue(keys: string[], current: any, cell: any) {
    if (keys.length === 0) return current;
    const clone = {...current};
    let pointer = clone;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (i === keys.length - 1) {
        pointer[key] = cell; // Final assignment
      } else {
        pointer[key] = {...(pointer[key] || {})}; // Clone nested object if it exists
        pointer = pointer[key]; // Move deeper
      }
    }
    return clone;
  }

  getFilteredDataTypeValues(groupMetric: MetricGroup, s7DataTypeValues: any[]) {
    if (groupMetric.transportConfiguration?.function) {
      let blacklist: string[] = this.gmService.s7FunctionValues.find((x: any) => x.value === groupMetric.transportConfiguration.function).typeBlackList;
      if (Array.isArray(blacklist) && blacklist.length > 0) {
        return s7DataTypeValues.filter((x: any) => !blacklist.includes(x.value));
      }
    }
    return s7DataTypeValues;
  }

  protected override getFullDataType(dataTypeName: string): DataType | undefined {
    return this.gmService.s7DataTypeValues.find(dt => dt.value === dataTypeName);
  }
}
